import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router'
import { Box, Stack, Heading, Text, Button, ResponsiveStack, Loading, EmptyBox, Icon, Card } from '@/components/lib'
import { FaPlus } from 'react-icons/fa'
import api from '../../../api'
import utils from '../../../utils/utils'
import useStore from '../../../store'
import useTitle from '../../../hooks/useTitle'

import PackList from './PackList'
import PackTypeForm from './PackTypeForm'

export default function Packs () {
  const [packTypes, setPackTypes] = useState([])
  const [selectedPackTypeId, setSelectedPackTypeId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [packTypeFormOpen, setPackTypeFormOpen] = useState(false)
  const [editingPackType, setEditingPackType] = useState(null)
  const { id: trialId } = useParams()
  const { user, trial } = useStore()
  useTitle('Packs', trial)

  useEffect(() => {
    if (!trialId) return
    setLoading(true)
    api.packs.getPackTypesForTrial(trialId, (d) => {
      setPackTypes(d.packTypes || [])
      setLoading(false)
      setSelectedPackTypeId(d.packTypes?.[0]?._id ?? null)
    }, () => setLoading(false))
  }, [trialId])

  const selectedPackType = packTypes.find((pt) => pt._id === selectedPackTypeId)

  function onPackTypeCreated (packType) {
    setPackTypes((prev) => [...prev, packType])
    setSelectedPackTypeId(packType._id)
    setPackTypeFormOpen(false)
  }

  function onPackTypeUpdated (packType) {
    setPackTypes((prev) => prev.map((pt) => (pt._id === packType._id ? packType : pt)))
    setEditingPackType(null)
  }

  function onPackTypeDeleted (packTypeId) {
    setPackTypes((prev) => prev.filter((pt) => pt._id !== packTypeId))
    if (selectedPackTypeId === packTypeId) setSelectedPackTypeId(null)
  }

  if (loading) return <Box textAlign='center'><Loading /></Box>

  return (
    <Stack gap={5}>
      <Box>
        <Heading as='h2' size='lg'>Packs</Heading>
        <Text>Manage pack types and packs for this trial.</Text>
      </Box>

      <ResponsiveStack gap={6} alignItems='stretch' breakpoint='lg'>
        <Card
          variant='elevated'
          minW='220px'
          maxW='280px'
        >
          <Stack gap={4}>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
              <Text fontWeight='bold' fontSize='sm'>Pack types</Text>
              {utils.canManageTrial(user, trial) && (
                <Button size='xs' colorPalette='primary' onClick={() => { setEditingPackType(null); setPackTypeFormOpen(true) }}>
                  <Icon as={FaPlus} /> Add
                </Button>
              )}
            </Stack>
            {packTypes.length === 0
              ? (
                <EmptyBox
                  icon='box'
                  title='No pack types'
                  description='Add a pack type to get started.'
                />
                )
              : (
                <Stack gap={2}>
                  {packTypes.map((pt) => (
                    <Stack
                      direction='row' alignItems='center' justifyContent='space-between' key={pt._id}
                      p={2}
                      rounded='md'
                      cursor='pointer'
                      bg={selectedPackTypeId === pt._id ? 'primary.solid' : 'transparent'}
                      color={selectedPackTypeId === pt._id ? 'fg.onAccent' : 'fg.muted'}
                      _hover={{ bg: selectedPackTypeId === pt._id ? 'primary.solid' : 'bg.muted' }}
                      onClick={() => setSelectedPackTypeId(pt._id)}
                    >
                      <Text fontSize='sm' fontWeight={selectedPackTypeId === pt._id ? 'bold' : 'normal'}>{pt.name}</Text>
                    </Stack>
                  ))}
                </Stack>
                )}
          </Stack>
        </Card>

        <Box flex={1} minW={0}>
          {selectedPackType
            ? (
              <PackList
                trialId={trialId}
                packType={selectedPackType}
                onEditPackType={() => setEditingPackType(selectedPackType)}
                onPackTypeUpdated={onPackTypeUpdated}
                onDeletePackType={onPackTypeDeleted}
                canManage={utils.canManageTrial(user, trial)}
              />
              )
            : (
              <EmptyBox
                icon='pack'
                title='Select a pack type'
                description='Choose a pack type from the sidebar to view and manage its packs, or add a new pack type to get started.'
              />
              )}
        </Box>
      </ResponsiveStack>

      {packTypeFormOpen && (
        <PackTypeForm
          trialId={trialId}
          onClose={() => setPackTypeFormOpen(false)}
          onCreated={onPackTypeCreated}
        />
      )}
      {editingPackType && (
        <PackTypeForm
          trialId={trialId}
          packType={editingPackType}
          onClose={() => setEditingPackType(null)}
          onUpdated={onPackTypeUpdated}
        />
      )}
    </Stack>
  )
}
